import { NODE_MERGE_D, DEFAULT_H } from "../constants";
import { Geo } from "../utils/geo";
import { snapWP, intP, mkId } from "../utils/coords";

// ─── Initial state ────────────────────────────────────────────────────────────
export const mkEmpty = () => ({
  nodes: {}, edges: {}, halfEdges: {}, faces: {}, openings: {}, rooms: {}, opLog: [], version: 3,
  project: { name: "Untitled Project", client: "", address: "", date: new Date().toLocaleDateString("en-IN") },
  _healLog: [],
  layerStates: {},
  constraints: {},

  // ── Drawing mode ─────────────────────────────────────────────────────────
  drawingMode: "architectural",

  // ── Geometric primitives ──────────────────────────────────────────────────
  circles:   {},
  arcs:      {},
  splines:   {},
  polylines: {},

  // ── Hatch fills per entity ────────────────────────────────────────────────
  faceHatches:     {},
  circleHatches:   {},
  polylineHatches: {},

  // ── User-defined blocks ───────────────────────────────────────────────────
  blockDefs: {},   // { [id]: BlockDefinition }
  blockRefs: {},   // { [id]: BlockReference }

  // ── Multi-floor ───────────────────────────────────────────────────────────
  floorIndex: 0,   // current floor (0=ground, 1=first, etc.)
  floors: {        // per-floor DCEL states (merged on render)
    0: null,       // null = use main state for floor 0
  },

  // ── Layer system ──────────────────────────────────────────────────────────
  layers: {
    walls:       { id:"walls",       name:"Walls",         color:"#3b82f6", visible:true,  locked:false, type:"system" },
    openings:    { id:"openings",    name:"Openings",      color:"#e8a838", visible:true,  locked:false, type:"system" },
    rooms:       { id:"rooms",       name:"Rooms",         color:"#4ade80", visible:true,  locked:false, type:"system" },
    electrical:  { id:"electrical",  name:"Electrical",    color:"#facc15", visible:true,  locked:false, type:"user"   },
    plumbing:    { id:"plumbing",    name:"Plumbing",      color:"#22d3ee", visible:true,  locked:false, type:"user"   },
    furniture:   { id:"furniture",   name:"Furniture",     color:"#a78bfa", visible:true,  locked:false, type:"user"   },
    structural:  { id:"structural",  name:"Structural",    color:"#f97316", visible:true,  locked:false, type:"user"   },
    annotations: { id:"annotations", name:"Annotations",   color:"#94a3b8", visible:true,  locked:false, type:"user"   },
    dimensions:  { id:"dimensions",  name:"Dimensions",    color:"#000000", visible:true,  locked:false, type:"user"   },
    site:        { id:"site",        name:"Site / Boundary",color:"#84cc16",visible:true,  locked:false, type:"user"   },
  },

  // ── Symbols (electrical, plumbing, furniture) ─────────────────────────────
  // { id, type, layerId, x, y, rotation, scale, label, faceId }
  symbols: {},

  // ── Annotations (text, leaders, callouts) ─────────────────────────────────
  // { id, layerId, x, y, text, leaderPts: [{x,y}], style: "text"|"callout"|"leader" }
  annotations: {},

  // ── Manual dimension overrides ────────────────────────────────────────────
  // { id, layerId, ptA: {x,y}, ptB: {x,y}, offset, text, orientation }
  manualDims: {},

  // ── Structural column grid ─────────────────────────────────────────────────
  // { id, axis: "x"|"y", position, label }
  gridLines: {},

  // ── Drawing settings (persisted so DrawingView restores correctly) ─────────
  drawing: {
    scale:        100,
    sheetKey:     "A3",
    northRotation:0,
    drawingNo:    "PC-FP-01",
    sectionCuts:  [],   // [{ id, x1,y1,x2,y2, label, lookDir }]
  },
});

// ─── Node helpers ─────────────────────────────────────────────────────────────
export function closestNode(p, nodes, maxD = NODE_MERGE_D) {
  let best = null, bd = maxD * maxD;
  for (const n of Object.values(nodes)) {
    const d = Geo.dsq(p, n);
    if (d < bd) { bd = d; best = n; }
  }
  return best;
}

export function getOrCreate(rawP, nodes) {
  const p = intP(snapWP(rawP));
  // First check if rawP itself already has a very close node (endpoint reuse)
  const ex = closestNode(p, nodes);
  if (ex) {
    // Return the exact existing node position so chains weld perfectly
    return { id: ex.id, nodes, point: { x: ex.x, y: ex.y } };
  }
  const id = mkId("n");
  return { id, nodes: { ...nodes, [id]: { id, x: p.x, y: p.y, halfEdgeIds: [] } }, point: p };
}

// ─── Edge primitives ──────────────────────────────────────────────────────────
export function addEdgePrim(aId, bId, type, thick, height, st) {
  if (aId === bId) return st;
  const na = st.nodes[aId]; if (!na) return st;
  for (const hid of na.halfEdgeIds) {
    const he = st.halfEdges[hid];
    if (he && st.halfEdges[he.twinId]?.originId === bId) return st;
  }
  const abId = mkId("he"), baId = mkId("he"), eId = mkId("e");
  const nb = st.nodes[bId] || { id: bId, x: 0, y: 0, halfEdgeIds: [] };
  return {
    ...st,
    nodes: {
      ...st.nodes,
      [aId]: { ...na, halfEdgeIds: [...na.halfEdgeIds, abId] },
      [bId]: { ...nb, halfEdgeIds: [...(nb.halfEdgeIds || []), baId] },
    },
    edges: {
      ...st.edges,
      [eId]: { id: eId, halfEdgeA: abId, halfEdgeB: baId, type: type || "wall", thickness: thick || 230, height: height || DEFAULT_H, materialId: "BRICK" },
    },
    halfEdges: {
      ...st.halfEdges,
      [abId]: { id: abId, originId: aId, twinId: baId, edgeId: eId, nextId: null, faceId: null },
      [baId]: { id: baId, originId: bId, twinId: abId, edgeId: eId, nextId: null, faceId: null },
    },
  };
}

export function removeEdgePrim(eId, st) {
  const e = st.edges[eId]; if (!e) return st;
  const ab = st.halfEdges[e.halfEdgeA], ba = st.halfEdges[e.halfEdgeB];
  if (!ab || !ba) return st;
  const nn = { ...st.nodes };
  if (nn[ab.originId]) nn[ab.originId] = { ...nn[ab.originId], halfEdgeIds: nn[ab.originId].halfEdgeIds.filter(h => h !== ab.id) };
  if (nn[ba.originId]) nn[ba.originId] = { ...nn[ba.originId], halfEdgeIds: nn[ba.originId].halfEdgeIds.filter(h => h !== ba.id) };
  const nh = { ...st.halfEdges }; delete nh[ab.id]; delete nh[ba.id];
  const ne = { ...st.edges }; delete ne[eId];
  const nop = { ...st.openings };
  for (const [oid, op] of Object.entries(nop)) if (op.edgeId === eId) delete nop[oid];
  return { ...st, nodes: nn, edges: ne, halfEdges: nh, openings: nop };
}

export function splitEdgePrim(eId, midP, st) {
  const e = st.edges[eId]; if (!e) return { st, mid: null };
  const ab = st.halfEdges[e.halfEdgeA], ba = st.halfEdges[e.halfEdgeB];
  if (!ab || !ba) return { st, mid: null };
  const aId = ab.originId, bId = ba.originId;
  let s = removeEdgePrim(eId, st);
  const { id: midId, nodes } = getOrCreate(midP, s.nodes);
  s = { ...s, nodes };
  s = addEdgePrim(aId, midId, e.type, e.thickness, e.height, s);
  s = addEdgePrim(midId, bId, e.type, e.thickness, e.height, s);
  return { st: s, mid: midId };
}
